/**
 * The global, placeable Relewise recommendation row
 * (sections/spejder-recommendations.liquid). Loaded by that section only when
 * the Relewise theme setting is on. sections/spejder-expert.liquid hosts the
 * same [data-relewise-recs] contract for its "no picks selected" tier.
 *
 * One component, five request types, chosen per instance in the theme editor:
 *
 *   personal      PersonalProduct                   — per-visitor
 *   popular       PopularProducts                   — 7/14/30-day window
 *   similar       SimilarProducts                   — data similarity, needs a product
 *   viewed_after  ProductsViewedAfterViewingProduct — needs a product
 *   cart          PurchasedWithMultipleProducts     — seeded from the cart lines
 *
 * PopularProducts is the universal safety net. `personal` has nothing to work
 * with for a visitor Relewise cannot identify (`userIsAnonymous` counts a
 * consented TEMPORARY id as identified, which is the point — the app's
 * auto-tracking gives an ordinary consenting visitor a history long before
 * they log in); `similar` and `viewed_after` have no seed anywhere but a
 * product page; `cart` has none on an empty cart. Each falls through to
 * popular rather than rendering a short row.
 *
 * Nothing here is fatal. The section server-renders the merchant's curated
 * products as the baseline, so every failure path — no consent, Relewise down,
 * empty result, handle Relewise knows but Shopify does not — simply leaves that
 * baseline on screen. Cards are only swapped in once we hold real ones.
 *
 * Multiple instances per page are supported: each [data-relewise-recs] carries
 * its own type, limit and Displayed At Location and resolves independently.
 */
!(function () {
  'use strict';

  /* The section emits this script tag itself, so a page carrying N instances
     executes this file N times — and each run would otherwise resolve every
     block on the page, firing N x N requests at Relewise. Claim each block
     before working on it: re-execution then finds nothing left to do, while a
     section added later in the theme editor is still picked up by the run its
     own tag triggers. */
  var blocks = [].slice.call(document.querySelectorAll('[data-relewise-recs]'))
    .filter(function (el) {
      if (el.hasAttribute('data-relewise-recs-claimed')) return false;
      el.setAttribute('data-relewise-recs-claimed', '');
      return true;
    });
  if (!blocks.length) return;

  /* Every row loads behind the shared skeleton ([data-relewise-hold], see
     spejder.css "Recommendation rows"): its curated baseline is held under
     it, or — with no baseline — it server-renders empty card shells. Every
     exit path ends here. `swapped` false means no real cards arrived: the
     hold is released onto the baseline, and a row that only had shells is
     collapsed (with its section, where the section has nothing else to show)
     rather than left as a heading over an empty strip. The theme editor keeps
     it on screen so it stays configurable. */
  function settle(block, swapped) {
    block.setAttribute('data-relewise-ready', '');
    if (swapped) return;
    var outer = block.querySelector('[data-relewise-recs-outer]');
    var hasBaseline = outer && outer.querySelector('[data-grid-item]:not([data-relewise-skeleton-card])');
    if (hasBaseline || (window.Shopify && Shopify.designMode)) return;
    block.hidden = true;
    var host = block.closest('[data-relewise-recs-collapse]');
    if (host) host.hidden = true;
  }

  var cfg = window.spejder && window.spejder.relewise;
  if (!cfg || !cfg.enabled || !window.Relewise) {
    blocks.forEach(function (block) { settle(block, false); });
    return;
  }

  var handleKey = cfg.shop + '_ShopifyHandle';

  /* Consent-correct identity via the shared helper (assets/relewise-batch.js),
     which waits for the app embed's global instead of testing for it now. */
  var userPromise = window.spejder.relewiseUser
    ? window.spejder.relewiseUser()
    : Promise.resolve(Relewise.UserFactory.anonymous());

  /* Every row on the page goes out in ONE batched request, deduplicated
     across rows by Relewise (assets/relewise-batch.js); the direct Recommender
     call is only the fallback for when that file did not load. `method` is the
     single-request Recommender method the request would otherwise use. */
  function recommend(method, request) {
    if (window.spejder.relewiseBatch) return window.spejder.relewiseBatch.recommend(request);
    return new Relewise.Recommender(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl })[method](request);
  }

  /* Every builder takes the same settings object and returns handles only —
     the card markup itself comes from Shopify, so no other property is worth
     asking for. */
  function prepare(builder, limit) {
    return builder
      .setNumberOfRecommendations(limit)
      .setSelectedProductProperties({ dataKeys: [handleKey] });
  }

  function popular(user, opt) {
    var b = new Relewise.PopularProductsBuilder(opt.settings(user))
      /* sinceMinutesAgo is REQUIRED — PopularProducts returns nothing at all
         without it (docs §7). The section exposes 7/14/30 days. */
      .sinceMinutesAgo(opt.days * 24 * 60);
    return recommend('recommendPopularProducts', prepare(b, opt.limit).build());
  }

  function personal(user, opt) {
    var b = new Relewise.PersonalProductRecommendationBuilder(opt.settings(user));
    return recommend('recommendPersonalProducts', prepare(b, opt.limit).build());
  }

  function similar(user, opt) {
    var b = new Relewise.SimilarProductsProductBuilder(opt.settings(user))
      .product({ productId: opt.productId })
      /* Takes a mutator callback over a zero-weighted default, not a settings
         object. The weights mean "same kind of thing, same corner of the
         catalogue, comparable money" — name and category carry it, price
         breaks ties. Brand stays 0 deliberately: the dataset has no brand
         entities (docs/relewise-implementation.md §4), so weighting it would
         silently do nothing. */
      .setEvaluationSettings(function (s) {
        s.significanceOfSimilaritiesInDisplayName = 5;
        s.significanceOfCommonImmediateParentCategories = 5;
        s.significanceOfCommonParentsParentCategories = 2;
        s.significanceOfSimilarSalesPrice = 3;
      })
      .filters(function (f) { return f.addProductIdFilter(opt.productId, true); });
    return recommend('recommendSimilarProducts', prepare(b, opt.limit).build());
  }

  function viewedAfter(user, opt) {
    var b = new Relewise.ProductsViewedAfterViewingProductBuilder(opt.settings(user))
      .product({ productId: opt.productId })
      .filters(function (f) { return f.addProductIdFilter(opt.productId, true); });
    return recommend('recommendProductsViewedAfterViewingProduct', prepare(b, opt.limit).build());
  }

  function purchasedWithCart(user, opt) {
    var b = new Relewise.PurchasedWithMultipleProductsBuilder(opt.settings(user))
      .addProducts(opt.cartIds.map(function (id) { return { productId: id }; }))
      /* Never recommend what is already in the cart. */
      .filters(function (f) { return f.addProductIdFilter(opt.cartIds, true); });
    return recommend('recommendPurchasedWithMultipleProducts', prepare(b, opt.limit).build());
  }

  /* Which request runs, and what has to be true for it to be possible at all.
     A failing `guard` is not an error — it means this page cannot serve that
     type (a "lignende produkter" block dropped on the homepage, a cart block
     on an empty cart), so the row quietly serves popular instead. */
  var TYPES = {
    personal: { run: personal, guard: function (o) { return !o.anonymous; } },
    popular: { run: popular, guard: function () { return true; } },
    similar: { run: similar, guard: function (o) { return !!o.productId; } },
    viewed_after: { run: viewedAfter, guard: function (o) { return !!o.productId; } },
    cart: { run: purchasedWithCart, guard: function (o) { return o.cartIds.length > 0; } },
  };

  function handlesOf(result) {
    return ((result && result.recommendations) || []).map(function (rec) {
      return rec.data && rec.data[handleKey] && rec.data[handleKey].value;
    }).filter(Boolean);
  }

  function fetchCards(handles, limit) {
    return Promise.all(handles.slice(0, limit).map(function (h) {
      return fetch('/products/' + h + '?view=relewise-card')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (cards) {
      /* A handle Relewise knows but Shopify does not still answers 200 with an
         EMPTY card shell (no <img>, no product link) — status alone cannot tell
         them apart, so filter on the product link the shell lacks. Same trap as
         the blank card in "Senest set". */
      return cards.filter(function (html) {
        return html && html.indexOf('href="/products/') !== -1;
      });
    });
  }

  function render(block, cards) {
    var outer = block.querySelector('[data-relewise-recs-outer]');
    if (!outer || !cards.length) return settle(block, false);

    /* Replace the whole <grid-slider>, not the row inside it. The element
       registers an IntersectionObserver over its slides when it connects, so
       swapping only the row's contents leaves it watching deleted nodes —
       no card gets `is-visible`, the prev arrow never disables at scroll 0
       (it floats over the first card), and clicking an arrow is a no-op
       because the handler looks up `[data-grid-item].is-visible`. Recreating
       the element fires destroy() then init(), rebuilding the arrows and
       their observers against the cards actually on screen. */
    /* A host section with its own row geometry (sections/spejder-expert.liquid)
       names its classes on the block, so the swapped-in row is markup-identical
       to the one it server-renders; the defaults are this section's own row. */
    var sliderClass = block.getAttribute('data-slider-class') || '';
    var rowClass = block.getAttribute('data-row-class') || 'grid grid--slider grid--mobile-slider';
    var cardClass = block.getAttribute('data-card-class') || '';
    if (cardClass) {
      cards = cards.map(function (html) {
        return '<div class="' + cardClass + '" data-grid-item>' + html + '</div>';
      });
    }

    outer.innerHTML =
      '<grid-slider' + (sliderClass ? ' class="' + sliderClass + '"' : '') + ' align-arrows>' +
        '<div class="' + rowClass + '" data-grid-slider data-relewise-recs-row>' +
          cards.join('') +
        '</div>' +
      '</grid-slider>';

    outer.querySelectorAll('[data-aos]').forEach(function (el) { el.classList.add('aos-animate'); });

    settle(block, true);
    /* No head-arrow bookkeeping: that pair carries `data-spejder-arrows-async`,
       so the shared handler's MutationObserver re-syncs it on this swap. */
  }

  blocks.forEach(function (block) {
    var opt = {
      limit: parseInt(block.getAttribute('data-limit'), 10) || 8,
      location: block.getAttribute('data-location') || 'Recommendations Block',
      days: parseInt(block.getAttribute('data-days'), 10) || 14,
      productId: cfg.productId || '',
      cartIds: (block.getAttribute('data-cart-ids') || '').split(',').filter(Boolean),
    };
    opt.settings = function (user) {
      return {
        language: cfg.language,
        currency: cfg.currency,
        displayedAtLocation: opt.location,
        user: user,
      };
    };

    var chosen = TYPES[block.getAttribute('data-type')] || TYPES.personal;

    userPromise
      .then(function (user) {
        opt.anonymous = typeof Relewise.userIsAnonymous === 'function'
          ? Relewise.userIsAnonymous(user)
          : true;

        if (!chosen.guard(opt)) return popular(user, opt);

        return chosen.run(user, opt).then(function (result) {
          /* A guard only proves the request was possible, not that it had
             anything to say — a first-time visitor has an id but no history,
             and a niche product may have no recorded co-views. Fall back
             rather than render a short row. */
          return handlesOf(result).length ? result : popular(user, opt);
        });
      })
      .then(function (result) {
        var handles = handlesOf(result);
        if (!handles.length) return settle(block, false);
        return fetchCards(handles, opt.limit).then(function (cards) { render(block, cards); });
      })
      .catch(function () { settle(block, false); /* keep the curated baseline */ });
  });
})();
